import { useBatchProgress, useBatchTracking } from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { useStreamState } from '@ctcells/realtime'
import { useEffect, useRef } from 'react'

import { useActiveBatchSource } from '../../shell/activeBatchSource.ts'
import { isTerminalBatch } from '../../shell/batchStripModel.ts'
import { useStripBatch } from '../../shell/stripPick.ts'

import { batchTransferring } from './chamberProgress.ts'
import { deviceKeyOfModuleType, type MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * 모니터링의 모듈 선택이 **「지금 그 배치를 돌리고 있는 모듈」을 따라가게** 하는 두 조각.
 *
 * eric 지시(2026-09-21): 「해당 배치가 실행중일 때, 예를 들면 염색기가 실행 중이면 염색기 탭이
 * 활성화, 원심분리기가 돌면 원심분리기 탭이 활성화 되고 있어야 해.」
 *
 * 🔴 **출처가 작업 줄 3행과 «같다».** 둘 다 구독 진행 프레임(`selectBatchProgress`)의
 *    `module_id` + `typeByModule` 를 읽는다. 작업 줄이 「염색기」라고 적는 순간 모니터링 선택도
 *    염색기여야 하고, 다른 축을 쓰면 두 자리가 **다른 말을 한다.**
 * 🔴 **모듈 상태 어휘로 고르지 않는다.** 「`IDLE` 이 아닌 모듈」로 판정하면 24값 중 무엇이
 *    「도는 중」인지를 **프론트가 정하는 것**이라 상태 추론이다. 게다가 6축이 핸드오프 중이거나
 *    두 모듈이 동시에 `IDLE` 이 아닐 때 갈린다.
 * 🔴 **`module_id` 문자열에서 종류를 읽지 않는다** — `deviceKeyOfModuleType` 참조.
 */

/**
 * 지금 그 배치를 돌리는 모듈의 화면 키. **모르면 `undefined`** 다.
 *
 * `undefined` 가 되는 경우는 다섯이고 처분은 하나다 — **선택을 건드리지 않는다.**
 * | 경우 | 왜 |
 * | --- | --- |
 * | 도는 작업이 없다 | 따라갈 곳이 없다 |
 * | **그 작업이 종착했다**(`completed`·`aborted`) | **끝난 회차에 「지금 도는 모듈」은 없다**(아래) |
 * | 진행 프레임이 아직 없다 | 서버가 말하기 전에 프론트가 정하면 추론이다 |
 * | 이벤트 구독이 `stale` | 마지막 값은 더 이상 「지금」이 아니다(`L1EventStream.stale`) |
 * | 모르는 종류(`ceiling_3axis`) | 화면에 그 탭이 없다 — 없는 곳으로 보내지 않는다 |
 *
 * 🔴 **6축은 `robot` 으로 돌아온다**(2026-10-07) — 진행 프레임이 아니라 «이 배치의 `robot` 자원 점유»로. 장비 칩 줄의 넷째 칸이다.
 *
 * ## 🔴 종착하면 따라가기를 멈춘다 (2026-09-22 실물)
 *
 * 배치가 `completed` 인데 염색기가 `WAITING_FOR_UNLOAD` 로 서 있었다. 그 회차의 마지막
 * 진행 프레임이 염색기를 가리킨 채 남아서 따라가기가 계속 염색기를 「도는 모듈」이라 했고,
 * **환자 카드 한 장이 두 말을 했다** — 염색 칸은 「진행 중 ◐」, 완료 칸은 「지남」.
 *
 * ⇒ **규칙은 하나다: 종착이면 따라가기 없음.** 모듈 상태 어휘를 새로 읽지 않고
 *   (그러면 프론트가 상태를 추론하는 것이다) **서버가 준 배치 상태** 하나로 끊는다.
 * ⚠ **모듈 상태가 사라지는 게 아니다.** 모듈 카드는 `ModuleSnapshot.state` 를 그대로 계속
 *   보인다 — 걷는 것은 **환자 카드의 «진행 중» 표지**뿐이다. 단계 줄은 그때부터
 *   서버가 준 단계 표지와 보고서 사건(`events[]`)만으로 선다.
 *
 * ⚠ **`demo` 에서는 요청을 보내지 않고 fixture 가 그 자리에 선다**(`useActiveBatchSource`) —
 *   작업 줄과 같은 규율이다. 종전에는 대타 없이 `skip` 만 해서 **목 화면에서 작업이 통째로
 *   비었고**, 그러면 진행 프레임을 물을 키(`batch_id`)조차 없어 따라가기가 죽는다.
 */
/**
 * **끝난 실행의 생애 사건** — 마지막 진행 프레임이 이것이면 그 모듈은 «지금» 돌지 않는다.
 * 🔴 서버가 한 말이다(`ExecutionStream.type` · 계약 「종착 = complete · error · cancelled · aborted」). 모듈 상태를 읽어 짐작하지 않는다.
 * `paused` 는 여기 없다 — 멈춘 것이지 끝난 것이 아니다(`stepActivityModel` 의 같은 표와 같은 결).
 */
const TERMINAL_EXECUTION_EVENTS: ReadonlySet<string> = new Set([
  'complete',
  'error',
  'cancelled',
  'aborted',
])

/** 마지막 진행 프레임이 끝난 실행인가 — `useRunningDeviceKey` 와 시료 카드(`useSampleFlow` 의 `settled`)가 같은 표를 쓴다 */
export function isTerminalExecutionEvent(eventType: string | undefined): boolean {
  return eventType !== undefined && TERMINAL_EXECUTION_EVENTS.has(eventType)
}

export function useRunningDeviceKey(): MonitoringDeviceKey | undefined {
  /* 🔴 작업 줄이 고르는 그 한 건이어야 한다 — 고르는 규칙을 복제하지 않고 같은 함수를 부른다 */
  const batch = useStripBatch(useActiveBatchSource().batches)
  const progress = useBatchProgress(batch?.batch_id)
  const eventsStale = useStreamState('events').stale
  /*
   * 🔴 **이송 구간은 6축이 «지금»이다**(eric 2026-10-07 「준비 단계에서.... 6축 로봇쪽 탭으로 포커싱 가능해? 탭 변경 말이야..」).
   *    6축 진행 프레임에는 `batch_id` 가 없어(dev2 실측 · 코어가 일부러 뺐다) 진행 프레임으로는 이 배치의 이송을 알 수 없다 —
   *    근거는 시료 카드와 같은 «이 배치 추적의 트레이가 `robot` · `in_transit`»(`batchTransferring` · `useSampleFlow` 와 같은 함수 ·
   *    같은 캐시 키)다(서버 v0.9.4 — 자원 점유가 걷혔다). 이송이 서면 다른 장비 진행보다 앞선다(카드의 「이송 중」과 같은 순서).
   * ⚠ 부가 조회다 — 못 받으면(실패) 6축으로 안 갈 뿐, 다른 장비 따라가기는 그대로다. demo · 끊김 · 도는 작업 없음이면 안 부른다.
   */
  const tracking = useBatchTracking({
    batchId: batch?.batch_id,
    skip: dataSourceFor('batches') !== 'live' || eventsStale || batch === undefined,
  })

  if (eventsStale) return undefined
  /* 🔴 끝난 회차에는 「지금 도는 모듈」이 없다 — 마지막 프레임을 「지금」으로 읽지 않는다 */
  if (isTerminalBatch(batch)) return undefined
  if (tracking.isSuccess && batchTransferring(tracking.data, batch?.batch_id)) return 'robot'
  /*
   * 🔴 **끝난 실행을 「지금」으로 쓰지 않는다**(2026-10-06 dev2 실측 · 팀장 배정). 원심의 배치 실행은 `complete`(176/176)로 끝나고
   *    같은 순간 원심이 `WAITING_FOR_TRAY_UNLOAD` · 실행 없음이 된다(04:23:15.853). 그 뒤 6축 이송에는 `batch_id` 가 없어(코어가 일부러
   *    뺐다) 이 배치의 다음 진행 프레임은 어셈블러가 시작할 때에야 온다 — 그동안 마지막 프레임(원심 `complete`)이 「지금 도는 장비」로
   *    남아 이송이 끝난 직후 1초쯤 「원심분리기 진행 중」이 다시 섰다(이송 중에는 `robot` 락이 그 위를 덮었다).
   *    ⇒ 마지막 프레임이 종착이면 모른다(`undefined`). 다음 장비를 짐작해 미리 세우지 않는다 — 그 장비의 진행 프레임이 오면 그때 선다.
   */
  if (isTerminalExecutionEvent(progress?.eventType)) return undefined
  return deviceKeyOfModuleType(progress?.moduleType)
}

export interface FollowRunningDeviceOptions {
  /** 지금 도는 모듈(`useRunningDeviceKey()`). `undefined` 면 **아무것도 하지 않는다** */
  running: MonitoringDeviceKey | undefined
  /** 지금 골라져 있는 모듈. 같으면 부르지 않는다 */
  selected: MonitoringDeviceKey
  /** 따라갈 때 부른다. 🔴 이동은 **`replace`** 여야 한다 — 자동 전환이 이력에 쌓이면 안 된다 */
  onFollow: (key: MonitoringDeviceKey) => void
  /** 따라가도 되는 키. 화면이 실제로 그리는 카드만 넘긴다(없는 탭으로 보내지 않는다) */
  selectable: readonly MonitoringDeviceKey[]
}

/**
 * 🔴 **상태가 아니라 «전환 사건»에 반응한다.**
 *
 * 같은 모듈이 계속 도는 동안에는 아무 일도 하지 않는다 — 그래야 사람이 다른 모듈을 눌러 보는
 * 중에 **1초 뒤 도로 튕겨 가지 않는다**(그러면 못 쓴다). 도는 모듈이 «바뀌는 순간»에만 옮긴다.
 *
 * | 사건 | 처분 |
 * | --- | --- |
 * | 처음으로 도는 모듈을 알게 됨 | 따라간다(화면에 들어왔을 때 도는 모듈이 골라져 있게) |
 * | 도는 모듈이 A → B 로 바뀜 | 따라간다 |
 * | 같은 모듈이 계속 돔 | **아무것도 안 한다** — 사람의 선택을 존중한다 |
 * | 도는 모듈을 모르게 됨(끊김 · 종료 · 6축) | **아무것도 안 하고 「마지막으로 안 것」도 안 지운다** |
 *
 * 🔴 마지막 칸이 중요하다. 끊겼다고 기억을 지우면, 다시 붙는 순간 **같은 모듈이 「새 전환」으로
 *    보여** 사람이 골라 둔 화면을 빼앗는다.
 */
export function useFollowRunningDevice({
  running,
  selected,
  onFollow,
  selectable,
}: FollowRunningDeviceOptions): void {
  /** 마지막으로 «안» 도는 모듈. 🔴 모르게 된 것(`undefined`)으로는 덮지 않는다 */
  const known = useRef<MonitoringDeviceKey | undefined>(undefined)
  /*
   * 이동 함수와 지금 선택은 렌더마다 새로 오지만 **따라가기를 다시 돌릴 이유는 아니다** —
   * 의존성에 넣으면 사람이 카드를 누를 때마다 효과가 돌아 도로 튕겨 간다. 최신 값만 들고 있는다.
   * ⚠ 갱신은 **효과 안**에서 한다(렌더 중 ref 쓰기 금지). 선언 차례상 아래 본 효과보다 먼저 돈다.
   */
  const follow = useRef(onFollow)
  const current = useRef(selected)
  useEffect(() => {
    follow.current = onFollow
    current.current = selected
  })

  const canFollow = running !== undefined && selectable.includes(running)

  useEffect(() => {
    if (!canFollow || running === undefined) return
    if (known.current === running) return
    known.current = running
    if (current.current === running) return
    follow.current(running)
  }, [canFollow, running])
}
